/* eslint-disable */
import { Link } from "react-router-dom";

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return {
      day: d.toLocaleDateString(undefined, { day: "numeric" }),
      month: d.toLocaleDateString(undefined, { month: "short" }),
      time: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
      weekday: d.toLocaleDateString(undefined, { weekday: "short" }),
      date: d,
    };
  } catch { return null; }
}

// Session 60.5 · Human-readable event range. Uses the viewer's local
// timezone so a 6:00pm-EDT event reads "6:00 PM" for the viewer if they're
// on EDT and "3:00 PM" if they're on PDT. If end < 24h after start on the
// same local day, render as "6:00 PM – 8:30 PM". If it spans days, render
// full dates on both ends.
function formatEventRange(startIso, endIso) {
  const s = fmtDate(startIso);
  if (!s) return null;
  const e = endIso ? fmtDate(endIso) : null;
  if (!e) return `${s.weekday}, ${s.month} ${s.day} · ${s.time}`;
  const sameDay =
    s.date.getFullYear() === e.date.getFullYear() &&
    s.date.getMonth() === e.date.getMonth() &&
    s.date.getDate() === e.date.getDate();
  if (sameDay) return `${s.weekday}, ${s.month} ${s.day} · ${s.time} \u2013 ${e.time}`;
  return `${s.month} ${s.day} ${s.time} \u2013 ${e.month} ${e.day} ${e.time}`;
}

const CLASSIFICATION_STYLE = {
  metier_presents: { label: "Métier Presents", color: "#B8913A" },
  partner: { label: "Partner Event", color: "#3A6BB8" },
  aggregated: { label: "Discovered", color: "rgba(0,0,0,0.45)" },
};

/**
 * EventCard, reusable card for the /events browse page and city-page strips.
 *
 * Session 60.23 · Card now routes to Métier's internal event detail page
 * (/events/local/:slug) — the canonical destination for share, calendar,
 * RSVP, and Commons discussion. External-organizer handoff happens on the
 * detail page, not from the card.
 *
 * Session 60.27 · `isPast` prop dims the card and swaps the CTA copy so
 * the Past Events section never looks like a live/upcoming event.
 */
export default function EventCard({ ev, compact = false, isPast = false }) {
  const d = fmtDate(ev.start_at);
  const range = formatEventRange(ev.start_at, ev.end_at);
  const classification = ev.classification || "aggregated";
  const badge = CLASSIFICATION_STYLE[classification] || CLASSIFICATION_STYLE.aggregated;
  const detailKey = ev.slug || ev.event_id;
  const to = `/events/local/${detailKey}`;
  return (
    <Link
      to={to}
      className={`m-event-card${compact ? " m-event-card-compact" : ""}${ev.featured ? " featured" : ""}${isPast ? " m-event-card-past" : ""}`}
      data-testid={`event-card-${ev.event_id}`}
      style={{ textDecoration: "none", color: "inherit", display: "block", opacity: isPast ? 0.78 : 1 }}
    >
      <article>
        {ev.image_url ? (
          <div className="m-event-card-img">
            <img src={ev.image_url} alt={ev.title} loading="lazy" />
            {d && (
              <div className="m-event-date-chip">
                <span className="m-event-date-month">{d.month}</span>
                <span className="m-event-date-day">{d.day}</span>
              </div>
            )}
            {ev.featured && <span className="m-event-featured">Featured</span>}
          </div>
        ) : (
          <div className="m-event-card-img m-event-card-img-empty">
            {d && (
              <div className="m-event-date-chip m-event-date-chip-solo">
                <span className="m-event-date-month">{d.month}</span>
                <span className="m-event-date-day">{d.day}</span>
              </div>
            )}
          </div>
        )}
        <div className="m-event-card-body">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 6 }}>
            {isPast && (
              <span
                data-testid={`event-past-pill-${ev.event_id}`}
                style={{
                  fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
                  padding: "2px 8px", borderRadius: 999, fontWeight: 700,
                  color: "#FFFFFF", background: "rgba(27,35,64,0.72)",
                }}
              >Past</span>
            )}
            <span
              data-testid={`event-classification-${ev.event_id}`}
              style={{
                fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
                padding: "2px 8px", borderRadius: 999, fontWeight: 700,
                color: badge.color, border: `1px solid ${badge.color}`,
              }}
            >{badge.label}</span>
            <span className="m-event-cat">{ev.category_label || "Community"}</span>
            {ev.cost && ev.cost !== "unknown" && (
              <span style={{ fontSize: 10, textTransform: "uppercase", opacity: 0.7 }}>
                · {ev.cost === "free" ? "Free" : "Paid"}
              </span>
            )}
            {ev.modality && ev.modality !== "in_person" && (
              <span style={{ fontSize: 10, textTransform: "uppercase", opacity: 0.7 }}>
                · {ev.modality === "virtual" ? "Virtual" : "Hybrid"}
              </span>
            )}
          </div>
          <h3 className="m-event-title">{ev.title}</h3>
          {!compact && ev.description && (
            <p className="m-event-desc">{(ev.description || "").slice(0, 160)}{ev.description && ev.description.length > 160 ? "…" : ""}</p>
          )}
          <div className="m-event-meta">
            {range && <span data-testid={`event-range-${ev.event_id}`}>{range}</span>}
            {ev.venue && <span>· {ev.venue}</span>}
            {ev.city && <span>· {ev.city}</span>}
          </div>
          <div className="m-event-actions">
            <span className="m-btn-ghost m-event-cta" data-testid={`event-view-${ev.event_id}`}>
              {isPast ? "View recap →" : "View event →"}
            </span>
            {ev.organizer && <span className="m-event-organizer" data-testid={`event-org-${ev.event_id}`}>by {ev.organizer}</span>}
          </div>
        </div>
      </article>
    </Link>
  );
}
